import { useState } from 'react';
import { useEmailOutbox, useSendDigests } from '../useAdmin';
import type { EmailOutboxRow, SendDigestsResult } from '../adminApi';
import { AuthApiError } from '../../account/authApi';
import './AdminEmailOutbox.css';

/**
 * Admin email outbox + send-digests (F15 + F16). The dev-stub email
 * provider captures every send into an in-memory tail; the SES
 * adapter mirrors there too. Operator can also trigger an immediate
 * digest pass.
 */
export function AdminEmailOutbox(): JSX.Element {
  const { data, isLoading, isError, error, refetch, isFetching } = useEmailOutbox();
  const sendDigests = useSendDigests();
  const [digestResult, setDigestResult] = useState<SendDigestsResult | null>(null);

  async function runDigests(dryRun: boolean) {
    const result = await sendDigests.mutateAsync({ dryRun });
    setDigestResult(result);
  }

  return (
    <section className="bl-admin-email" aria-label="Email outbox">
      <header className="bl-admin-email__header">
        <div>
          <h2>Email outbox</h2>
          <p className="bl-admin-email__sub">
            Newest 50 messages the platform sent. Bounded in memory so very-old sends roll off; the
            dev-stub provider also writes files on disk.
          </p>
        </div>
        <div className="bl-admin-email__actions">
          <button type="button" onClick={() => void refetch()} disabled={isFetching}>
            {isFetching ? 'Refreshing…' : 'Refresh'}
          </button>
          <button
            type="button"
            onClick={() => void runDigests(true)}
            disabled={sendDigests.isPending}
          >
            Dry-run digests
          </button>
          <button
            type="button"
            onClick={() => {
              if (window.confirm('Send digests to every eligible adult immediately?')) {
                void runDigests(false);
              }
            }}
            disabled={sendDigests.isPending}
            className="bl-admin-email__primary"
          >
            {sendDigests.isPending ? 'Sending…' : 'Send digests now'}
          </button>
        </div>
      </header>

      {(isError || sendDigests.error) && (
        <p className="bl-admin-email__error" role="alert">
          {sendDigests.error instanceof AuthApiError
            ? sendDigests.error.detail || sendDigests.error.message
            : (error || sendDigests.error) instanceof Error
              ? ((error || sendDigests.error) as Error).message
              : 'Could not complete the request.'}
        </p>
      )}

      {digestResult && (
        <p className="bl-admin-email__digest-result" role="status" aria-live="polite">
          {digestResult.sent > 0 ? `${digestResult.sent} digests sent` : 'No digests dispatched'}
          {digestResult.skipped > 0 && ` · ${digestResult.skipped} skipped`}.
          {digestResult.rows.length > 0 && (
            <span className="bl-admin-email__digest-detail">
              {' '}
              {digestResult.rows.map((r) => `${r.to} (${r.status})`).join(', ')}
            </span>
          )}
          <button
            type="button"
            onClick={() => setDigestResult(null)}
            className="bl-admin-email__digest-dismiss"
            aria-label="Dismiss digest result"
          >
            ×
          </button>
        </p>
      )}

      {isLoading ? (
        <p className="bl-admin-email__notice">Loading outbox…</p>
      ) : (data?.rows.length ?? 0) === 0 ? (
        <p className="bl-admin-email__notice">
          Outbox is empty. Trigger a digest above to seed it.
        </p>
      ) : (
        <ul className="bl-admin-email__list">
          {data!.rows.map((row, i) => (
            <Entry key={`${row.sent_at}-${i}`} row={row} />
          ))}
        </ul>
      )}
    </section>
  );
}

function Entry({ row }: { row: EmailOutboxRow }): JSX.Element {
  return (
    <li className="bl-admin-email__entry">
      <div className="bl-admin-email__entry-head">
        <span className="bl-admin-email__subject">{row.subject}</span>
        <span className="bl-admin-email__provider">{row.provider}</span>
        <time className="bl-admin-email__when" dateTime={row.sent_at}>
          {formatDate(row.sent_at)}
        </time>
      </div>
      <p className="bl-admin-email__to">to: {row.to}</p>
      <pre className="bl-admin-email__body">{row.body_text}</pre>
    </li>
  );
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}
